import { useEffect, useMemo, useState } from 'react';
import { Box, Container } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link } from 'react-router-dom';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import {
  KC_DICTIONARY_BASE,
  KC_GUIDE_BASE,
  KC_HOME_PATH,
  getDictionaryIndex,
} from './repository';
import { KC_COLORS } from './theme';
import { KC_TRUNCATE, truncateAtWord } from './truncateAtWord';
import './knowledgeCenter.css';

function TermLink({ href, children }) {
  if (!href) {
    return (
      <span
        className="break-words"
        style={{ color: KC_COLORS.navy700, fontSize: '15px', lineHeight: '22px' }}
      >
        {children}
      </span>
    );
  }
  return (
    <Link
      to={href}
      className="group inline-flex max-w-full items-start gap-1.5 no-underline font-semibold transition-all hover:gap-2"
      style={{ color: KC_COLORS.primaryNavy, fontSize: '15px', lineHeight: '22px' }}
    >
      <span className="min-w-0 break-words group-hover:underline">{children}</span>
      <ArrowForwardRoundedIcon
        className="shrink-0 mt-0.5"
        sx={{ fontSize: 16, color: KC_COLORS.teal, opacity: 0.9 }}
      />
    </Link>
  );
}

export function KnowledgeCenterDictionary() {
  const dictionary = useMemo(() => getDictionaryIndex(), []);
  const [activeLetter, setActiveLetter] = useState(
    dictionary.activeLetters?.[0] || 'A'
  );

  useEffect(() => {
    if (!dictionary.activeLetters?.includes(activeLetter)) {
      setActiveLetter(dictionary.activeLetters?.[0] || 'A');
    }
  }, [activeLetter, dictionary.activeLetters]);

  const activeGroup =
    dictionary.groups.find((group) => group.letter === activeLetter) || null;

  const scrollToLetter = (letter) => {
    if (!dictionary.activeLetters.includes(letter)) return;
    setActiveLetter(letter);
    const el = document.getElementById(`dict-letter-${letter}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <PageSeoHelmet
        pageTitle="Insurance AI Dictionary (A–Z) | Charlee.ai"
        metaDescription="Browse the Charlee.ai Insurance AI Dictionary A–Z index of executive reference guides for Property & Casualty insurance."
        canonicalPath={KC_DICTIONARY_BASE}
      />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="knowledge-center-dictionary-page">
          <Box
            className="w-full pb-12 md:pb-16"
            style={{
              background:
                'linear-gradient(180deg, #F4F8FA 0%, #FFFFFF 38%, #FFFFFF 100%)',
            }}
          >
            <Container maxWidth="lg">
              <div className="pt-28 md:pt-32 text-left">
                <nav aria-label="Breadcrumb" className="mb-5">
                  <ol
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 kc-type-body font-semibold"
                    style={{ color: KC_COLORS.navy700 }}
                  >
                    <li>
                      <Link to="/" className="no-underline" style={{ color: KC_COLORS.navy700 }}>
                        Home
                      </Link>
                    </li>
                    <li style={{ color: KC_COLORS.gray600 }}>&gt;</li>
                    <li>
                      <Link
                        to={KC_HOME_PATH}
                        className="no-underline"
                        style={{ color: KC_COLORS.navy700 }}
                      >
                        Knowledge Center
                      </Link>
                    </li>
                    <li style={{ color: KC_COLORS.gray600 }}>&gt;</li>
                    <li style={{ color: KC_COLORS.gray600 }}>Dictionary</li>
                  </ol>
                </nav>

                <p
                  className="m-0 kc-type-caption font-bold uppercase tracking-[0.08em]"
                  style={{ color: KC_COLORS.teal }}
                >
                  Insurance AI Dictionary
                </p>
                <h1
                  className="kc-type-h1 m-0 mt-3"
                  style={{ color: KC_COLORS.primaryNavy }}
                >
                  A–Z Glossary
                </h1>
                <p
                  className="m-0 mt-4 max-w-3xl kc-type-body text-[15px] leading-6 sm:text-[16px] sm:leading-[26px]"
                  style={{ color: KC_COLORS.gray600 }}
                >
                  Explore executive reference definitions for Property & Casualty insurance AI.
                  Select a letter to jump to matching terms, then open any entry to read the full guide.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2.5 sm:gap-3">
                  <span
                    className="inline-flex items-center rounded-full border bg-white px-3 py-1.5 kc-type-caption font-semibold"
                    style={{ borderColor: KC_COLORS.border, color: KC_COLORS.navy700 }}
                  >
                    {dictionary.termCount} terms
                  </span>
                  <Link
                    to={KC_GUIDE_BASE}
                    className="inline-flex items-center gap-1 font-semibold no-underline"
                    style={{ color: KC_COLORS.teal, fontSize: '14px' }}
                  >
                    Browse all reference guides
                    <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                  </Link>
                </div>
              </div>

              <div
                className="kc-dictionary-az mt-8 md:mt-10 sticky top-16 sm:top-20 z-20 rounded-2xl border bg-white/95 backdrop-blur-sm px-2.5 py-2.5 sm:px-4 sm:py-3.5 shadow-[0_8px_24px_rgba(10,29,58,0.08)]"
                style={{ borderColor: KC_COLORS.border }}
              >
                <div className="kc-dictionary-az-track flex flex-nowrap sm:flex-wrap gap-1 sm:gap-1.5 justify-start overflow-x-auto overscroll-x-contain pb-0.5 -mx-0.5 px-0.5">
                  {(dictionary.letters || []).map((letter) => {
                    const isEnabled = dictionary.activeLetters.includes(letter);
                    const isActive = activeLetter === letter;
                    return (
                      <button
                        key={letter}
                        type="button"
                        disabled={!isEnabled}
                        onClick={() => scrollToLetter(letter)}
                        className="inline-flex h-8 w-8 shrink-0 sm:min-w-8 sm:w-auto sm:px-1.5 items-center justify-center rounded-md kc-type-caption font-bold transition-colors"
                        style={{
                          backgroundColor: isActive
                            ? KC_COLORS.teal
                            : isEnabled
                              ? KC_COLORS.tealLight
                              : 'transparent',
                          color: isActive
                            ? KC_COLORS.white
                            : isEnabled
                              ? KC_COLORS.primaryNavy
                              : '#D1D5DB',
                          cursor: isEnabled ? 'pointer' : 'default',
                          border: 'none',
                        }}
                        aria-pressed={isActive}
                        aria-label={`Jump to letter ${letter}`}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 md:mt-10 space-y-8 md:space-y-10 text-left">
                {dictionary.groups.map((group) => (
                  <section
                    key={group.letter}
                    id={`dict-letter-${group.letter}`}
                    className="scroll-mt-28 sm:scroll-mt-36"
                  >
                    <div className="flex items-end gap-2.5 sm:gap-3 mb-3.5 sm:mb-4">
                      <h2
                        className="m-0 font-bold text-[28px] leading-8 sm:text-[32px] sm:leading-9"
                        style={{
                          color: group.letter === activeLetter ? KC_COLORS.teal : KC_COLORS.primaryNavy,
                        }}
                      >
                        {group.letter}
                      </h2>
                      <span
                        className="kc-type-caption font-semibold pb-1"
                        style={{ color: KC_COLORS.gray600 }}
                      >
                        {group.terms.length} {group.terms.length === 1 ? 'term' : 'terms'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {group.terms.map((item) => (
                        <div
                          key={`${group.letter}-${item.term}`}
                          className="min-w-0 rounded-2xl border bg-white p-3.5 sm:p-4 md:p-5 transition-all hover:shadow-[0_10px_28px_rgba(10,29,58,0.08)]"
                          style={{
                            borderColor:
                              group.letter === activeLetter
                                ? 'rgba(0,155,170,0.35)'
                                : KC_COLORS.border,
                          }}
                        >
                          <TermLink href={item.href}>{item.term}</TermLink>
                          {item.subtitle ? (
                            <p
                              className="m-0 mt-1.5 break-words"
                              style={{
                                color: KC_COLORS.teal,
                                fontSize: '12px',
                                lineHeight: '16px',
                                fontWeight: 600,
                              }}
                            >
                              {item.subtitle}
                            </p>
                          ) : null}
                          {item.summary ? (
                            <p
                              className="m-0 mt-2 kc-listing-summary"
                              style={{
                                color: KC_COLORS.gray600,
                                fontSize: '13px',
                                lineHeight: '20px',
                              }}
                            >
                              {truncateAtWord(item.summary, KC_TRUNCATE.threeLines)}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              {activeGroup ? (
                <p className="sr-only">
                  Currently viewing letter {activeGroup.letter} with{' '}
                  {activeGroup.terms.length} terms.
                </p>
              ) : null}
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterDictionaryPage() {
  return <KnowledgeCenterDictionary />;
}
